import { useToast } from "@/hooks/use-toast";
import {
  audioService,
  type CreateAudioPayload,
  type UpdateAudioPayload,
} from "@/lib/api/audio.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const AUDIO_QUERY_KEY = ["/audio"];

export function useAudioList(filters: Parameters<typeof audioService.list>[0]) {
  return useQuery({
    queryKey: [...AUDIO_QUERY_KEY, filters],
    queryFn: () => audioService.list(filters),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useAudio(id: string | null) {
  return useQuery({
    queryKey: [...AUDIO_QUERY_KEY, id],
    queryFn: () => audioService.get(id!),
    enabled: !!id,
    retry: false,
  });
}

export function useAudioByVersionId(versionId: string | null) {
  return useQuery({
    queryKey: [...AUDIO_QUERY_KEY, "version", versionId],
    queryFn: () => audioService.getByVersionId(versionId!),
    enabled: !!versionId,
    retry: false,
  });
}

export function useCreateAudio() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateAudioPayload) => audioService.create(data),
    onSuccess: (result) => {
      queryClient.removeQueries({ queryKey: AUDIO_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: AUDIO_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...AUDIO_QUERY_KEY, result.id],
      });
      toast({ title: "Audio created" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to create audio",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateAudio() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      versionId,
      data,
    }: {
      versionId: string;
      data: UpdateAudioPayload;
    }) => audioService.update(versionId, data),
    onSuccess: (result) => {
      queryClient.removeQueries({ queryKey: AUDIO_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: AUDIO_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...AUDIO_QUERY_KEY, result.id],
      });
      queryClient.invalidateQueries({
        queryKey: [...AUDIO_QUERY_KEY, "version", result.versionId],
      });
      toast({ title: "Audio saved" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to save audio",
        variant: "destructive",
      });
    },
  });
}

export function useSubmitAudio() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (audioId: string) => audioService.submit(audioId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: AUDIO_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: AUDIO_QUERY_KEY });
      toast({ title: "Audio submitted for approval" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to submit audio",
        variant: "destructive",
      });
    },
  });
}

export function useArchiveAudio() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (audioId: string) => audioService.archive(audioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUDIO_QUERY_KEY });
      toast({ title: "Audio archived" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to archive audio",
        variant: "destructive",
      });
    },
  });
}

export function useRestoreAudio() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (audioId: string) => audioService.restore(audioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUDIO_QUERY_KEY });
      toast({ title: "Audio restored to draft" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to restore audio",
        variant: "destructive",
      });
    },
  });
}

export function useCreateAudioVersion() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => audioService.createNewVersion(id),
    onSuccess: (result) => {
      queryClient.removeQueries({ queryKey: AUDIO_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: AUDIO_QUERY_KEY });
      toast({ title: `Version ${result.version} created` });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to create new version",
        variant: "destructive",
      });
    },
  });
}
